import { Dispatch, MutableRefObject, SetStateAction, memo } from 'react'
import isEqual from 'react-fast-compare'

import { decodeHtml } from '@nx/decode-html'
import { FireCarousel } from '@nx/fire/carousel'
import { FireImage } from '@nx/fire/image'
import { useNewImageSubdomain } from '@nx/helpers'
import { useJWPlayer } from '@nx/hooks/use-jwplayer'
import { SmartlingAttributes } from '@nx/smartling'
import { getTranslate } from '@nx/translations'

import {
  SingleImageButton,
  StyledGreyOverlay,
  StyledImageButton,
  StyledImageContainer,
  StyledMainCarouselContainer,
  StyledPlayedIcon,
  StyledThumbnailCarousel,
  StyledThumbnailCarouselContainer,
  StyledThumbnailImage,
  StyledVideoContainer,
  ThumbnailCaption,
} from './Carousel.styles'
import translate from './Carousel.translate.json'
import { JumpToVideo } from './JumpToVideo'
import {
  CarouselSlide,
  SlideType,
  SliderAction,
} from './LightboxCarousel.types'

export interface CarouselProps {
  carouselSlides: CarouselSlide[]
  videos: string[]
  description: string
  setIsLightBoxOpen: Dispatch<SetStateAction<boolean>>
  slideDispatch: Dispatch<SliderAction>
  moveToItemRef: MutableRefObject<((page: number) => void) | undefined>
}

const { text } = getTranslate(translate)
const carouselImageOptions = `&height=430&quality=90`
const thumbnailsImageOptions = `&height=100&quality=90`
const totalViewableThumbnails = 5

export const Carousel = memo(
  ({
    carouselSlides,
    videos,
    description,
    setIsLightBoxOpen,
    slideDispatch,
    moveToItemRef,
  }: CarouselProps) => {
    const { addNewImageSubdomain } = useNewImageSubdomain()

    const { jwVideoControllers } = useJWPlayer({
      loadScript: !!videos.length,
      videoIds: videos,
    })

    const totalSlides = carouselSlides.length
    const anySlideHasCaption = carouselSlides.some((slide) => slide.caption)
    const hasOneSlide = totalSlides === 1
    const shouldShowThumbnailArrows = totalSlides > totalViewableThumbnails

    return hasOneSlide ? (
      <SingleImageButton
        onClick={() => {
          setIsLightBoxOpen(true)
        }}
      >
        <FireImage
          src={addNewImageSubdomain(
            `${carouselSlides[0].src}${carouselImageOptions}`
          )}
          alt={carouselSlides[0].alt}
          style={{
            width: 'auto',
          }}
          height={430}
          width={702}
          {...SmartlingAttributes.noTranslate}
        />
      </SingleImageButton>
    ) : (
      <>
        <StyledMainCarouselContainer>
          <FireCarousel
            fullWidthItems="always"
            moveToRef={moveToItemRef}
            onHandleScrollCallback={() => {
              if (Object.keys(jwVideoControllers).length) {
                for (const [, videoController] of Object.entries(
                  jwVideoControllers
                )) {
                  videoController.stop()
                }
              }
            }}
          >
            {carouselSlides.map((slide, index) => {
              if (slide.type === SlideType.image) {
                return (
                  <StyledImageContainer
                    key={index}
                    onClick={() => {
                      slideDispatch({
                        type: 'UPDATE_SLIDE',
                        payload: { newSlide: index },
                      })
                      setIsLightBoxOpen(true)
                    }}
                  >
                    <FireImage
                      src={addNewImageSubdomain(slide.src)}
                      data-testid={`carousel-image-slide-${index}`}
                      alt={slide.alt}
                      height={430}
                      width={702}
                    />
                  </StyledImageContainer>
                )
              } else {
                return (
                  <StyledVideoContainer key={slide.src}>
                    <div
                      id={slide.src}
                      data-testid={`carousel-video-slide-${index}`}
                    />
                  </StyledVideoContainer>
                )
              }
            })}
          </FireCarousel>
        </StyledMainCarouselContainer>
        <StyledThumbnailCarouselContainer $withCaption={anySlideHasCaption}>
          <StyledThumbnailCarousel
            $showThumbnailArrows={shouldShowThumbnailArrows}
            showPaginationDots={false}
            alignment="start"
          >
            {carouselSlides.map((slide, index) => (
              <StyledImageButton
                key={index}
                onClick={() => {
                  moveToItemRef.current?.(index + 1)
                  slideDispatch({
                    type: 'UPDATE_SLIDE',
                    payload: { newSlide: index },
                  })
                }}
                $thumbnailIndex={index}
              >
                <StyledThumbnailImage
                  priority
                  alt={text('thumbnailOf', { alt: slide.alt })}
                  data-testid={`carousel-thumbnail-${index}`}
                  src={
                    slide.type === SlideType.video
                      ? `https://content.jwplatform.com/thumbs/${slide.src}-320.jpg`
                      : addNewImageSubdomain(
                          `${slide.src}${thumbnailsImageOptions}`
                        )
                  }
                  height={100}
                  width={110}
                />
                {slide.type === 'video' && (
                  <StyledGreyOverlay
                    data-testid={`carousel-thumbnail-${index}`}
                  >
                    <StyledPlayedIcon />
                  </StyledGreyOverlay>
                )}
                {slide.caption && (
                  <ThumbnailCaption $level="XS">
                    {decodeHtml(slide.caption)}
                  </ThumbnailCaption>
                )}
              </StyledImageButton>
            ))}
          </StyledThumbnailCarousel>
        </StyledThumbnailCarouselContainer>
        {/* Mobile only */}
        {videos.length ? (
          <JumpToVideo
            alt={`${description} video`}
            src={videos[0]}
            onClick={() => {
              // video is always the second slide
              moveToItemRef.current?.(2)
            }}
          />
        ) : null}
        {/* Mobile only */}
      </>
    )
  },
  isEqual
)
